import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@/components/common/Toast';
import { StoreImageCarousel } from '@/components/store/StoreImageCarousel';
import { CartButton } from '@/components/common/CartButton';
import { StoreDetailTabs } from '@/components/store/StoreDetailTabs';
import { StoreProductList } from '@/components/store/StoreProductList';
import { PickupTimeNotice } from '@/components/store/PickupTimeNotice';
import { useStoreDetail } from '@/hooks/useStoreDetail';
import { SEO } from '@/components/seo/SEO';
import { generateStoreSEO } from '@/utils/seo';
import type { StoreDetailPageProps, OrderType } from './store-detail.types';
import type { RestaurantWithProduct } from '@/types';

export function StoreDetailPage({ storeId: propStoreId, readonly = false }: StoreDetailPageProps = {}) {
  const { storeId: paramStoreId } = useParams<{ storeId: string }>();
  const actualStoreId = propStoreId || paramStoreId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast, ToastContainer } = useToast();
  
  // 유효한 주문유형 목록
  const validOrderTypes: OrderType[] = ['DEADLINE_DISCOUNT', 'RESERVATION_DISCOUNT', 'REGULAR_ORDER'];
  
  // URL에서 초기 주문 유형 읽기
  const urlOrderType = searchParams.get('orderType') as OrderType | null;
  const initialOrderType = urlOrderType && validOrderTypes.includes(urlOrderType) 
    ? urlOrderType 
    : 'DEADLINE_DISCOUNT';
  
  // 커스텀 훅으로 모든 상태와 로직 관리
  const {
    restaurant,
    products,
    isLoading,
    selectedOrderType,
    isFavorite,
    isCheckingFavorite,
    hidingProducts,
    calculatedRating,
    calculatedTotalReviews,
    isLoadingReviewStats,
    setSelectedOrderType,
    handleFavoriteToggle
  } = useStoreDetail({ 
    storeId: actualStoreId, 
    initialOrderType,
    readonly, 
    showToast 
  });

  // 이벤트 핸들러들
  const handleOrderTypeChange = (newOrderType: OrderType) => {
    setSelectedOrderType(newOrderType);
    // URL 업데이트 (replace로 히스토리 교체)
    navigate(`/store/${actualStoreId}?orderType=${newOrderType}`, { replace: true });
  };

  const handleProductClick = (productId: string) => {
    if (readonly) return;
    if (selectedOrderType === 'DEADLINE_DISCOUNT' && restaurant?.deadline_discount_enabled === false) {
      showToast('마감할인 오픈 대기 중인 매장입니다.');
      return;
    }
    navigate(`/store/${actualStoreId}/product/${productId}?orderType=${selectedOrderType}`);
  };

  const handleReviewClick = () => {
    if (readonly) return;
    navigate(`/store/${actualStoreId}/reviews`);
  };

  const handleStoreInfoClick = () => {
    navigate(`/store/${actualStoreId}/info`);
  };

  const handleFavoriteClick = async () => {
    await handleFavoriteToggle();
    if (!readonly && isFavorite) {
      setTimeout(() => {
        if (window.confirm('단골가게 페이지에서 확인하시겠습니까?')) {
          navigate('/favorites');
        }
      }, 1000);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">매장 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">매장 정보를 찾을 수 없습니다</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  // 동적 SEO 생성
  const seoProps = restaurant ? generateStoreSEO({
    ...restaurant,
    pickup_instructions: restaurant.pickup_instructions || '픽업 정보 없음'
  } as RestaurantWithProduct) : null;

  return (
    <>
      {seoProps && <SEO {...seoProps} />}
      <div className="min-h-screen bg-white">
        {/* 헤더 - 이미지 위에 오버레이 */}
        {!readonly && (
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 py-3 mt-[52px]">
          <button
            onClick={() => navigate(-1)}
            className="p-2 bg-black bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all"
          >
            <img src="/icons/icon_back.svg" alt="뒤로 가기" width="24" height="24" className="filter brightness-0 invert" />
          </button>
        </div>
      )}

      {/* 매장 이미지 캐러셀 */}
      <StoreImageCarousel 
        images={restaurant.image_url && restaurant.image_url.trim() ? [restaurant.image_url] : []}
        storeName={restaurant.name}
      />

      {/* 매장 정보 카드 */}
      <div className="mx-4 -mt-4 mb-6 relative z-[5]">
        <div className="bg-white rounded-lg border-2 border-[#86DB89] p-3">
          {/* 매장명과 즐겨찾기 */}
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-2xl font-semibold text-[#222222]">{restaurant.name}</h1>
            {!readonly && (
              <button
                onClick={handleFavoriteClick}
                disabled={isCheckingFavorite}
                className="hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 p-1"
              >
                <img
                  src={isFavorite ? "/icons/icon_favorites_selected.svg" : "/icons/icon_favorites.svg"}
                  alt="즐겨찾기"
                  className="w-6 h-6"
                  style={{
                    filter: isFavorite ? 'brightness(0) saturate(100%) invert(43%) sepia(96%) saturate(1352%) hue-rotate(87deg) brightness(119%) contrast(119%)' : 'brightness(0) saturate(100%) invert(20%)'
                  }}
                />
              </button>
            )}
          </div>
          
          {/* 평점과 리뷰 */}
          {readonly ? (
            <div className="bg-[#F5F5F5] rounded-lg px-1 py-1 mb-3 inline-flex items-center gap-1">
              <span className="text-base font-semibold text-black">{calculatedRating.toFixed(1)}</span>
              <span className="text-base text-black">/</span>
              <span className="text-base text-black">5</span>
              <span className="text-base text-black">(리뷰 {calculatedTotalReviews}개)</span>
              {isLoadingReviewStats && <span className="text-xs text-gray-500 ml-1">...</span>}
            </div>
          ) : (
            <button 
              onClick={handleReviewClick}
              className="bg-[#F5F5F5] rounded-lg px-1 py-1 mb-3 inline-flex items-center gap-1 hover:bg-gray-200 transition-colors"
            >
              <span className="text-base font-semibold text-black">{calculatedRating.toFixed(1)}</span>
              <span className="text-base text-black">/</span>
              <span className="text-base text-black">5</span>
              <span className="text-base text-black">(리뷰 {calculatedTotalReviews}개)</span>
              {isLoadingReviewStats && <span className="text-xs text-gray-500 ml-1">...</span>}
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="ml-1">
                <path d="M7.5 5L12.5 10L7.5 15" stroke="#222222" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          
          {/* 대표메뉴 */}
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[14px] min-[376px]:text-base font-semibold text-[#222222]">대표메뉴</span>
            <div className="flex items-center gap-1 text-[14px] min-[376px]:text-base text-[#222222] flex-wrap">
              <span>{restaurant.bestmenu1 || restaurant.bestmenu}</span>
              {restaurant.bestmenu2 && (
                <>
                  <span>|</span>
                  <span>{restaurant.bestmenu2}</span>
                </>
              )}
              {restaurant.bestmenu3 && (
                <>
                  <span>|</span>
                  <span>{restaurant.bestmenu3}</span>
                </>
              )}
            </div>
          </div>
          
          {/* 주소 */}
          <div className="flex items-center gap-3 min-[376px]:gap-9 mb-3">
            <span className="text-[14px] min-[376px]:text-base font-semibold text-[#222222]">주소</span>
            <span className="text-[14px] min-[376px]:text-base text-[#222222] flex-1">{restaurant.address}</span>
          </div>

          {/* 취소/환불 정책 */}
          <div className="mt-1 mb-2">
            <div className="text-[13px] font-semibold text-gray-700 mb-1">취소/환불 정책</div>
            <div className="text-[13px] min-[376px]:text-[14px] text-gray-600 leading-relaxed">
              <p>• 마감할인: 결제 후 매장 수락 전까지 취소 가능(수락 후 불가)</p>
              <p>• 예약할인: 수령시간 2시간 전까지 취소 가능</p>
              <p>• 일반주문: 매장 수락 전까지 취소 가능(수락 후 불가)</p>
            </div>
          </div>
          
          {/* 매장 정보 더보기 */}
          <button 
            onClick={handleStoreInfoClick}
            className="flex items-center gap-1 text-[14px] min-[376px]:text-base text-[#222222]"
          >
            <span>매장 정보 더보기</span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M7.5 5L12.5 10L7.5 15" stroke="#222222" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* 주문 유형 선택 탭 */}
      <StoreDetailTabs 
        selectedOrderType={selectedOrderType}
        onOrderTypeChange={handleOrderTypeChange}
      />

      {/* 수령시간 노출 (마감할인 탭 전용) */}
      <PickupTimeNotice 
        restaurant={restaurant}
        products={products}
        selectedOrderType={selectedOrderType}
      />

      {selectedOrderType === 'DEADLINE_DISCOUNT' && restaurant?.deadline_discount_enabled === false && (
        <div className="mx-4 mt-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
          마감할인 오픈 대기 중인 매장입니다. 주문이 일시 중단되었습니다.
        </div>
      )}

      {/* 상품 목록 */}
      <StoreProductList 
        products={products}
        hidingProducts={hidingProducts}
        readonly={readonly}
        onProductClick={handleProductClick}
      />
      
      {/* 플로팅 장바구니 버튼 */}
      {!readonly && (
        <div 
          className="fixed bottom-20 right-6 z-[9999]"
          style={{ position: 'fixed', bottom: '80px', right: '24px', zIndex: 9999 }}
        >
          <CartButton 
            variant="default" 
            size="large" 
            className="bg-white shadow-lg border border-gray-200 rounded-full hover:shadow-xl"
          />
        </div>
      )}
      
      <ToastContainer />
    </div>
    </>
  );
}
